/**
 * Sticky toast watcher for the active investigation's `dirty` flag.
 *
 * Fires on `dirty` false→true and on subsequent size changes; auto-dismisses
 * on save/discard. Renders nothing — purely a side-effect component, mounted
 * once per case so it observes adds from any view (graph expand, insights,
 * agent).
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { InvestigationsState } from '@/stores/investigations-store'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'

const TOAST_ID = 'investigation-dirty-changes'

interface NotifierSnapshot {
  dirty: boolean
  activeId: string | null
  activeName: string | undefined
  // `${nodes.length}|${edges.length}` — refreshes the toast on subsequent
  // edits so the user knows additional changes have stacked up.
  sizeFingerprint: string
}

function deriveSnapshot(s: InvestigationsState): NotifierSnapshot {
  if (!s.activeId) {
    return { dirty: false, activeId: null, activeName: undefined, sizeFingerprint: '' }
  }
  const inv = s.saved.find(i => i.id === s.activeId)
  return {
    dirty: s.dirty,
    activeId: s.activeId,
    activeName: inv?.title,
    sizeFingerprint: inv ? `${inv.graphView.nodes.length}|${inv.graphView.edges.length}` : '',
  }
}

export function InvestigationDirtyNotifier() {
  const { t } = useTranslation()
  const storeApi = useInvestigationsStoreApi()
  const { dirty, activeId, activeName, sizeFingerprint } = useStore(
    storeApi,
    useShallow(deriveSnapshot)
  )

  const wasDirty = useRef(false)
  const lastFingerprint = useRef(sizeFingerprint)

  useEffect(() => {
    if (wasDirty.current && !dirty) {
      toast.dismiss(TOAST_ID)
      wasDirty.current = false
      lastFingerprint.current = sizeFingerprint
      return
    }

    if (!dirty || !activeId) {
      lastFingerprint.current = sizeFingerprint
      return
    }

    const fingerprintChanged = sizeFingerprint !== lastFingerprint.current
    if (wasDirty.current && !fingerprintChanged) return

    const handleSave = async () => {
      try {
        await storeApi.getState().saveActive()
        toast.success(t('investigations.graph.dirtyToast.saveSuccess'))
      } catch (err) {
        toast.error(
          t('investigations.graph.dirtyToast.saveFailed', {
            message: (err as Error).message,
          })
        )
      }
    }

    const handleDiscard = () => {
      storeApi.getState().discardChanges()
      toast.message(t('investigations.graph.dirtyToast.discarded'))
    }

    toast(t('investigations.graph.dirtyToast.title'), {
      id: TOAST_ID,
      description: activeName
        ? t('investigations.graph.dirtyToast.description', { name: activeName })
        : undefined,
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: t('investigations.graph.dirtyToast.save'),
        onClick: () => {
          void handleSave()
        },
      },
      cancel: {
        label: t('investigations.graph.dirtyToast.discard'),
        onClick: handleDiscard,
      },
    })

    wasDirty.current = true
    lastFingerprint.current = sizeFingerprint
  }, [dirty, activeId, activeName, sizeFingerprint, storeApi, t])

  return null
}
